import { defineStore } from 'pinia';
import { ref } from 'vue';

import { BasketService } from '@apps/main/modules/api/basketService';
import { useFerryTicketCategoryUpsellStore } from '@apps/main/new-desktop/pages/tripOverview/stores/upsell/ferryTicketCategoryUpsellStore';

import {
  trackErrorAddingBasket,
  trackGoToCheckout,
} from '@libs/google-analytics-events';
import type {
  ActivityBookableOptionTicket,
  ActivityToBook,
  AvailableFerryIngredientResponse,
  AvailableFlightIngredientResponse,
  ItineraryIngredientResponse,
  ItineraryWithPrice,
  SelectedTicketInfo,
} from '@libs/trip-planner-interfaces';
import {
  isAvailableActivityIngredientResponse,
  isAvailableHotelIngredientResponse,
  isAvailableTransportIngredientResponse,
  isHotelIngredientResponse,
} from '@libs/search-service-smart-search';
import { SemboDate } from '@libs/sembo-dates';

import type { TokensForReplacement } from '../../../pages/tripOverview/models';
import { useRecipeStore } from '../recipe/recipeStore';
import { useCriteriaStore } from '../criteria/criteriaStore';
import { useItineraryStore } from '../itinerary/itineraryStore';

import {
  handleBookingError,
  BookingErrorType,
  replaceTokensBeforeGoingToCheckout,
} from './bookTripStoreLogic';
import type { BookingError } from './bookTripStoreLogic';

// TODO: move booking logic from tripPlannerViewModel
export const useBookTripStore = defineStore('BookTripStore', () => {
  const BASKET_SERVICE = new BasketService();

  const recipeStore = useRecipeStore();
  const itineraryStore = useItineraryStore();
  const ferryTicketCategoryUpsellStore = useFerryTicketCategoryUpsellStore();
  const criteriaStore = useCriteriaStore();

  // State
  const bookingInProgress = ref<boolean>(false);
  const bookingError = ref<BookingError | null>(null);

  // Actions
  async function navigateToCheckout(): Promise<void> {
    trackGoToCheckout();
    const tokensForReplacement: TokensForReplacement[] =
      ferryTicketCategoryUpsellStore.getUpsellTokens();
    bookingInProgress.value = true;

    const bookingErrorType = await book(tokensForReplacement);
    bookingError.value = bookingErrorType
      ? handleBookingError(bookingErrorType)
      : null;

    if (bookingErrorType) {
      bookingInProgress.value = false;
    }
  }

  function clearBookingError(): void {
    bookingError.value = null;
  }

  // Helper functions
  async function book(
    tokensForReplacement: TokensForReplacement[]
  ): Promise<BookingErrorType | null> {
    if (!itineraryStore.selectedItinerary) {
      return null;
    }
    const itineraryTokens: [string][] = [];
    try {
      (await getItineraryTokens(itineraryStore.selectedItinerary)).forEach(
        (token) => itineraryTokens.push([token])
      );
    } catch {
      return BookingErrorType.WrongNumberOfTokens;
    }
    try {
      const tokensAfterReplace = replaceTokensBeforeGoingToCheckout(
        itineraryTokens,
        tokensForReplacement
      );

      await BASKET_SERVICE.addTokensToBasket(tokensAfterReplace);
      //TODO: Forward information about itinerary following shop's ecommerce objects
      BASKET_SERVICE.goToCheckoutPage();
      bookingInProgress.value = false;
    } catch {
      trackErrorAddingBasket();
      return BookingErrorType.CommunicationError;
    }
    return null;
  }

  async function getItineraryTokens(
    itinerary: ItineraryWithPrice
  ): Promise<string[]> {
    const activitiesToBook: ActivityToBook[] = [
      ...itinerary.hotelOnlyItinerary,
      ...itinerary.ownStayOnlyItinerary,
    ].flatMap((hotelResponse) =>
      hotelResponse.subIngredientResponse.reduce(
        (
          availableActivitiesToBook: ActivityToBook[],
          activitySubIngredient
        ) => {
          if (isAvailableActivityIngredientResponse(activitySubIngredient)) {
            const code = activitySubIngredient.suggested.code;

            const dateOffset = new SemboDate(activitySubIngredient.date).diff(
              hotelResponse.date,
              'day'
            );

            const activity = recipeStore.stayOnlyRecipe
              .flatMap((stayIngredient) => stayIngredient.subIngredients)
              .find(
                (activityIngredient) =>
                  activityIngredient.code === code &&
                  activityIngredient.offset === dateOffset
              );

            if (!activity) {
              throw new Error('Activity not found in recipe');
            }

            availableActivitiesToBook.push({
              code,
              date: activitySubIngredient.date,
              // NOTE: Selected tickets are taken from itinerary response instead of recipe,
              // because of supplier token which can be different for each day.
              // We can't store supplier token in the recipe.
              tickets: activitySubIngredient.suggested.tickets.map(
                (ticket: ActivityBookableOptionTicket): SelectedTicketInfo => ({
                  timeSlotId: ticket.timeSlotId,
                  rateId: ticket.rateId,
                  quantity: ticket.quantity,
                  ageRangeId: ticket.ageRangeId,
                  supplierToken: ticket.supplierToken,
                })
              ),
            });
          }

          return availableActivitiesToBook;
        },
        []
      )
    );

    const activityBookingTokens = await BASKET_SERVICE.getActivityBookingTokens(
      activitiesToBook,
      criteriaStore.businessCriteria
    );

    const numberOfActivities = activitiesToBook.length;

    if (activityBookingTokens.length !== numberOfActivities) {
      return Promise.reject(
        new Error('Activity booking tokens do not match number of activities')
      );
    }

    function isBookableTransportIngredientResponse(
      ingredientResponse: ItineraryIngredientResponse
    ): ingredientResponse is
      | AvailableFlightIngredientResponse
      | AvailableFerryIngredientResponse {
      return (
        isAvailableTransportIngredientResponse(ingredientResponse) &&
        ingredientResponse.totalPrice.amount > 0 &&
        ingredientResponse.legIndex === 0
      );
    }

    const hotelAndFlightBookingTokens = itinerary.itinerary.reduce(
      (tokens: string[], ingredientResponse) => {
        if (isAvailableHotelIngredientResponse(ingredientResponse)) {
          tokens.push(ingredientResponse.suggested.addToBasketToken);
        }
        if (isBookableTransportIngredientResponse(ingredientResponse)) {
          tokens.push(
            `Type=flight&Identifier=${ingredientResponse.addToBasketToken}`
          );
        }

        return tokens;
      },
      []
    );

    const bookableIngredients = itinerary.itinerary.filter(
      (ingredient) =>
        isHotelIngredientResponse(ingredient) ||
        isBookableTransportIngredientResponse(ingredient)
    );
    const numberOfIngredients = bookableIngredients.length;

    if (hotelAndFlightBookingTokens.length !== numberOfIngredients) {
      return Promise.reject(
        new Error(
          'Hotel and flight booking tokens do not match number of ingredients'
        )
      );
    }

    return [...hotelAndFlightBookingTokens, ...activityBookingTokens];
  }

  return {
    // Actions
    clearBookingError,
    navigateToCheckout,

    // State
    bookingError,
    bookingInProgress,
  };
});
